// import { useContext, useEffect, useMemo } from "react";
// import { useNavigate, useLocation } from "react-router-dom";
// import { NotificationContext } from "../utils/common";
// import { getNotification } from "../redux/action/masterAction";
// import Layout from "../components/layout";
// import {
//   AdminMenu,
//   ClientMenu,
//   EmployeeMenu,
//   ManagerMenu,
// } from "../components/navbar/contants";
// import { Navigate } from "react-router-dom";

// const PrivateRoute = () => {
//   const routesRoles = useMemo(() => {
//     const UserType = localStorage.getItem("user_role");
//     const NonMenuItems = [
//       { path: "/finlo/change_password" },
//       { path: "/finlo/create_timesheet" },
//       { path: "/finlo/notifications" },
//     ];
//     const NavMenu =
//       UserType === "admin"
//         ? [...AdminMenu, ...NonMenuItems]
//         : UserType === "employee"
//         ? [...EmployeeMenu, ...NonMenuItems]
//         : UserType === "manager"
//         ? [...ManagerMenu, ...NonMenuItems]
//         : [...ClientMenu, ...NonMenuItems, { path: "/finlo/service_offers" }];
//     return NavMenu.map((d) => d.path);
//   }, []);

//   const { setNotifications } = useContext(NotificationContext);
//   let location = useLocation();

//   useEffect(() => {
//     if (localStorage.getItem("user_id")) {
//       const fetch = async () => {
//         const data = await getNotification();
//         setNotifications(data);
//       };
//       fetch();
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [location]);

//   return localStorage.getItem("user_id") ? (
//     <>
//       {routesRoles.includes(location.pathname) ? (
//         <Layout />
//       ) : (
//         <Navigate to={"/finlo/dashboard"} replace={true} />
//       )}
//     </>
//   ) : (
//     <Navigate
//       to={"/login"}
//       state={{ from: location.pathname }}
//       replace={true}
//     />
//   );
// };
// export default PrivateRoute;



import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useLocation, Navigate, matchPath } from "react-router-dom";
import { getRolePath } from "../utils/common";
import { useNotificationFeed } from "../hooks/useNotificationFeed";
import {
  setTabSession,
  setTabHeartbeatNow,
  isWithinNewTabGrace,
  clearNewTabGrace,
} from "../utils/tabHeartbeat";
import {
  clearAuthStorage,
  hasPersistedAuth,
  isSessionExpired,
} from "../utils/sessionAuth";
import {
  getAuthRole,
  hydrateAuthSession,
  startAuthTamperGuard,
} from "../utils/authSession";
import Layout from "../components/layout";
import {
  AdminMenu,
  ClientMenu,
  getEmployeeNavMenu,
  getManagerNavMenu,
} from "../components/navbar/contants";

const PrivateRoute = () => {
  const { refresh } = useNotificationFeed();
  const location = useLocation();
  const [userRole, setUserRole] = useState(() => getAuthRole());
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);

  useLayoutEffect(() => {
    let cancelled = false;
    startAuthTamperGuard();

    const finish = (authed, role) => {
      if (cancelled) return;
      if (role) setUserRole(role);
      setIsAuthed(authed);
      setAuthChecked(true);
    };

    (async () => {
      if (!hasPersistedAuth()) {
        finish(false);
        return;
      }

      if (isWithinNewTabGrace()) {
        clearNewTabGrace();
      }

      try {
        const session = await hydrateAuthSession({ force: true });
        if (cancelled) return;

        if (!session?.role || !session?.userId) {
          clearAuthStorage();
          finish(false);
          return;
        }

        if (isSessionExpired({ role: session.role })) {
          clearAuthStorage();
          finish(false);
          return;
        }

        setTabSession();
        setTabHeartbeatNow();
        finish(true, session.role);
      } catch (_) {
        clearAuthStorage();
        finish(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Keep role in sync if token/session is refreshed while app is open.
  useEffect(() => {
    if (!isAuthed) return undefined;
    const syncRole = () => {
      const role = getAuthRole();
      if (role) setUserRole(role);
    };
    window.addEventListener("focus", syncRole);
    return () => window.removeEventListener("focus", syncRole);
  }, [isAuthed]);

  // All hooks must be called before any conditional returns
  const routesRoles = useMemo(() => {
    // Add non-menu (private) routes here with role prefix
    const NonMenuItems = [
      { path: `/finlo/${userRole}/change_password` },
      { path: `/finlo/${userRole}/create_timesheet` },
      { path: `/finlo/${userRole}/notifications` },
      { path: `/finlo/${userRole}/client_details/:id` },
      { path: `/finlo/${userRole}/messages` },
      { path: `/finlo/${userRole}/tax_strategy` },
      { path: `/finlo/${userRole}/my_tax_team` },
      { path: `/finlo/${userRole}/profile` },
      { path: `/finlo/${userRole}/working_hours` },
      { path: `/finlo/${userRole}/pending_services` },
      { path: `/finlo/${userRole}/client_transfer` },
    ];

    const NavMenu =
      userRole === "admin"
        ? [...AdminMenu, ...NonMenuItems]
        : userRole === "employee"
        ? [...getEmployeeNavMenu(), ...NonMenuItems]
        : userRole === "manager"
        ? [...getManagerNavMenu(), ...NonMenuItems]
        : [
            ...ClientMenu,
            ...NonMenuItems,
            { path: `/finlo/${userRole}/service_offers` },
            { path: `/finlo/${userRole}/our_services` },
          ];

    return NavMenu.map((d) => d.path);
  }, [userRole]);

  // Fetch notifications once on login (when auth is established).
  // Refreshes happen in layout when a notification is clicked, and on the
  // notifications page / client-details notification tab on mount.
  useEffect(() => {
    if (!isAuthed) return;
    const fetch = async () => {
      await refresh();
    };
    fetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  // Check if path needs role prefix redirect (after all hooks)
  const pathSegments = location.pathname.split("/");
  const roleInPath = pathSegments[2]; // /finlo/:role/...
  
  // If path doesn't have role prefix or has wrong role, redirect
  if (pathSegments[1] === "finlo" && (!roleInPath || roleInPath !== userRole)) {
    const pathWithoutRole = location.pathname.replace(/^\/finlo\/[^/]+/, "/finlo");
    const correctPath = pathWithoutRole.replace("/finlo", `/finlo/${userRole}`);
    if (location.pathname !== correctPath) {
      return <Navigate to={correctPath} replace />;
    }
  }

  if (!authChecked) {
    return null;
  }

  const canAccess = routesRoles.some((p) =>
    matchPath({ path: p, end: false }, location.pathname)
  );

  return isAuthed ? (
    canAccess ? (
      <Layout />
    ) : (
      <Navigate to={getRolePath("/dashboard")} replace />
    )
  ) : (
    <Navigate
      to="/login"
      state={{ from: `${location.pathname}${location.search}` }}
      replace
    />
  );
};

export default PrivateRoute;
